import { useEffect, useState } from 'react'

import { useHasFeatureAccess } from '@/hooks/use-has-feature-access'
import { PLANS } from '@/lib/plans'
import { useContextStore } from '@/stores/context'

import { Enforce2FaDialog } from '../enforce-2fa-dialog'

/**
 * Global Enforce2FaProvider component
 * Renders a non-dismissible Enforce2FaDialog when the account enforces 2FA
 * for all users and the logged user has none configured. The dialog embeds
 * the full 2FA setup, so no route is suppressed.
 * Should be rendered once at the app layout level.
 */
export function Enforce2FaProvider() {
  const accountContext = useContextStore((s) => s.userAccountContext)

  // The backend disables the flag on plan cancel/downgrade
  // (Account.Sync2FaEnforcementWithPlan); this plan check is a
  // belt-and-suspenders guard against a transient pre-sync flag.
  const hasAccess = useHasFeatureAccess(PLANS.PRO)

  const isEnforcementActive =
    !!accountContext &&
    accountContext.accountInformation.enforce2Fa &&
    !accountContext.requiresAccountSetup &&
    hasAccess

  const needsSetup =
    isEnforcementActive && !accountContext.userAccountInformation.has2FaEnabled

  // Activating 2FA invalidates the user-account-context query, so
  // has2FaEnabled flips to true while the dialog is still showing the
  // one-time recovery codes. A mounted dialog therefore only closes through
  // its own onClose (or when enforcement itself stops applying), never
  // because the setup condition cleared.
  const [isDialogMounted, setIsDialogMounted] = useState(false)

  useEffect(() => {
    if (needsSetup) {
      setIsDialogMounted(true)
      return
    }
    if (!isEnforcementActive) {
      setIsDialogMounted(false)
    }
  }, [needsSetup, isEnforcementActive])

  if (!isDialogMounted) return null

  return <Enforce2FaDialog onClose={() => setIsDialogMounted(false)} />
}
